import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Trophy, BarChart3, History, 
  Settings, BookOpen, PenLine, Headphones, Mic,
  Calendar, Target, TrendingUp, Bell, Crown, Lock, Eye, EyeOff, Loader2,
  CreditCard, CheckCircle, XCircle, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const [statsRes, achievementsRes, historyRes] = await Promise.all([
        axios.get(`${API}/users/stats`),
        axios.get(`${API}/users/achievements`),
        axios.get(`${API}/users/history?limit=20`)
      ]);
      setStats(statsRes.data);
      setAchievements(achievementsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExerciseIcon = (type) => {
    switch (type) {
      case 'reading': return <BookOpen className="w-4 h-4 text-blue-500" />;
      case 'writing': return <PenLine className="w-4 h-4 text-green-500" />;
      case 'listening': return <Headphones className="w-4 h-4 text-purple-500" />;
      case 'speaking': return <Mic className="w-4 h-4 text-orange-500" />;
      default: return <Target className="w-4 h-4 text-slate-500" />;
    }
  };

  const tabs = [
    { id: 'stats', label: 'Estadísticas', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'subscription', label: 'Suscripción', icon: <Crown className="w-4 h-4" /> },
    { id: 'achievements', label: 'Logros', icon: <Trophy className="w-4 h-4" /> },
    { id: 'history', label: 'Historial', icon: <History className="w-4 h-4" /> },
    { id: 'settings', label: 'Seguridad', icon: <Lock className="w-4 h-4" /> }
  ];

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/users/password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Contraseña actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Cargando perfil...</div>
      </div>
    );
  }

  const unlockedCount = achievements.filter(a => a.unlocked).length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
            <span className="font-medium text-slate-900">Mi Perfil</span>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-white border border-slate-200 p-6 mb-6 animate-fade-in-up">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-['Playfair_Display'] text-2xl font-bold text-slate-900">
                  {user?.name}
                </h1>
                <p className="text-slate-600">{user?.email}</p>
                {user?.level && (
                  <span className={`level-badge level-${user.level.toLowerCase()} mt-2`}>
                    Nivel {user.level}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/notifications')}
                className="rounded-full"
                data-testid="notifications-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                Notificaciones
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/level-select')}
                className="rounded-full"
                data-testid="change-level-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Cambiar Nivel
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-full ${
                activeTab === tab.id 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-600'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in-up">
          {/* Stats Tab */}
          {activeTab === 'stats' && stats && (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-200 p-4">
                  <p className="text-3xl font-bold text-slate-900">{stats.completed_exercises}</p>
                  <p className="text-sm text-slate-600">Ejercicios Completados</p>
                </div>
                <div className="bg-white border border-slate-200 p-4">
                  <p className="text-3xl font-bold text-slate-900">{stats.average_score?.toFixed(0)}%</p>
                  <p className="text-sm text-slate-600">Promedio General</p>
                </div>
                <div className="bg-white border border-slate-200 p-4">
                  <p className="text-3xl font-bold text-slate-900">{stats.streak_days}</p>
                  <p className="text-sm text-slate-600">Días de Racha</p>
                </div>
                <div className="bg-white border border-slate-200 p-4">
                  <p className="text-3xl font-bold text-slate-900">{stats.total_time_minutes}</p>
                  <p className="text-sm text-slate-600">Minutos Practicados</p>
                </div>
              </div>

              {/* Section Progress */}
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                  Progreso por Sección
                </h3>
                <div className="space-y-4">
                  {[
                    { id: 'reading', name: 'Reading', icon: <BookOpen className="w-5 h-5" />, color: 'bg-blue-500' },
                    { id: 'writing', name: 'Writing', icon: <PenLine className="w-5 h-5" />, color: 'bg-green-500' },
                    { id: 'listening', name: 'Listening', icon: <Headphones className="w-5 h-5" />, color: 'bg-purple-500' },
                    { id: 'speaking', name: 'Speaking', icon: <Mic className="w-5 h-5" />, color: 'bg-orange-500' }
                  ].map((section) => (
                    <div key={section.id}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 ${section.color} text-white rounded-sm flex items-center justify-center`}>
                            {section.icon}
                          </div>
                          <span className="font-medium text-slate-900">{section.name}</span>
                        </div>
                        <span className="text-slate-600">
                          {(stats.section_progress?.[section.id] || 0).toFixed(0)}%
                        </span>
                      </div>
                      <Progress 
                        value={stats.section_progress?.[section.id] || 0} 
                        className="h-2"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="space-y-6">
              {/* Current Plan */}
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-orange-600" />
                  Tu Plan Actual
                </h3>
                
                {user?.subscription?.is_premium ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                        <Crown className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                          Premium 
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        </h4>
                        <p className="text-sm text-slate-600">
                          Plan {user.subscription.plan === 'yearly' ? 'Anual' : 'Mensual'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Estado</p>
                        <p className="font-medium text-green-600 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Activo
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Válido hasta</p>
                        <p className="font-medium text-slate-900">
                          {user.subscription.expires_at 
                            ? new Date(user.subscription.expires_at).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })
                            : 'Sin fecha de expiración'
                          }
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-4 mt-4">
                      <h4 className="font-medium text-slate-900 mb-3">Beneficios incluidos:</h4>
                      <ul className="space-y-2">
                        {[
                          'Acceso a todos los Sample Tests',
                          'Ejercicios ilimitados en todas las secciones',
                          'Feedback de IA en Writing y Speaking',
                          'Audio generado para Listening',
                          'Historial completo de resultados'
                        ].map((benefit, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-sm text-slate-500 mb-3">
                        ¿Necesitas ayuda con tu suscripción?
                      </p>
                      <a 
                        href="mailto:examreadyenglishhelp@gmail.com"
                        className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Contactar soporte
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-slate-500" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-slate-900">Plan Gratuito</h4>
                        <p className="text-sm text-slate-600">Acceso limitado a ejercicios</p>
                      </div>
                    </div>

                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Crown className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-slate-900">Actualiza a Premium</h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Desbloquea todos los Sample Tests, ejercicios ilimitados y funciones de IA.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {/* Monthly Plan */}
                      <div className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors">
                        <h4 className="font-bold text-slate-900">Mensual</h4>
                        <p className="text-3xl font-bold text-slate-900 mt-2">
                          €9.99<span className="text-sm font-normal text-slate-500">/mes</span>
                        </p>
                        <Button
                          onClick={() => navigate('/subscription?plan=monthly')}
                          className="w-full mt-4 bg-slate-900 hover:bg-slate-800 rounded-full"
                        >
                          Elegir Mensual
                        </Button>
                      </div>

                      {/* Yearly Plan */}
                      <div className="border-2 border-orange-400 rounded-lg p-4 relative">
                        <span className="absolute -top-3 left-4 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                          Ahorra 20%
                        </span>
                        <h4 className="font-bold text-slate-900">Anual</h4>
                        <p className="text-3xl font-bold text-slate-900 mt-2">
                          €95.99<span className="text-sm font-normal text-slate-500">/año</span>
                        </p>
                        <p className="text-xs text-slate-500 mt-1">Equivale a €8/mes</p>
                        <Button
                          onClick={() => navigate('/subscription?plan=yearly')}
                          className="w-full mt-4 bg-orange-500 hover:bg-orange-400 rounded-full"
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Elegir Anual
                        </Button>
                      </div>
                    </div>

                    <div className="text-center text-sm text-slate-500">
                      <CreditCard className="w-4 h-4 inline mr-1" />
                      Pago seguro con Stripe
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Achievements Tab */}
          {activeTab === 'achievements' && (
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-orange-600" />
                  Tus Logros
                </h3>
                <span className="text-sm text-slate-500">
                  {unlockedCount} de {achievements.length} desbloqueados
                </span>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {achievements.map((achievement) => (
                  <div 
                    key={achievement.id}
                    className={`p-4 rounded-sm border transition-all ${
                      achievement.unlocked 
                        ? 'bg-orange-50 border-orange-200' 
                        : 'bg-slate-50 border-slate-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{achievement.icon}</span>
                      <div>
                        <h4 className="font-medium text-slate-900">{achievement.name}</h4>
                        <p className="text-sm text-slate-500">{achievement.name_es}</p>
                        <p className="text-xs text-slate-400 mt-1">{achievement.description_es}</p>
                        {achievement.unlocked && achievement.unlocked_at && (
                          <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Desbloqueado: {new Date(achievement.unlocked_at).toLocaleDateString('es-ES')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-orange-600" />
                Historial de Ejercicios
              </h3>
              
              {history.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aún no has completado ningún ejercicio</p>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="mt-4 rounded-full"
                  >
                    Comenzar a Practicar
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((item) => (
                    <div 
                      key={item.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        {getExerciseIcon(item.exercise_type)}
                        <div>
                          <p className="font-medium text-slate-900 capitalize">
                            {item.exercise_type}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(item.completed_at).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        item.score >= 70 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {item.score}%
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Settings/Security Tab */}
          {activeTab === 'settings' && (
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Lock className="w-5 h-5 text-orange-600" />
                Cambiar Contraseña
              </h3>
              
              <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Contraseña Actual
                  </label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Tu contraseña actual"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nueva Contraseña
                  </label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Confirmar Nueva Contraseña
                  </label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repite la nueva contraseña"
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className="w-full bg-slate-900 hover:bg-slate-800 rounded-full mt-6"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cambiando...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Cambiar Contraseña
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
